import { supabase } from '@/infrastructure/supabase/client'
import { Page } from '@/domain/model/Page'
import { Block } from '@/domain/model/Block'
import type { IPageRepository, CreatePageInput } from '@/application/port/out/IPageRepository'
import type { BlockType, BlockProps, BlockSettings, PageMeta, Tables, TablesInsert, TablesUpdate, Json } from '@brix/types'

function toPageEntity(row: Tables<'pages'>): Page {
  return Page.reconstitute({
    id: row.id,
    siteId: row.site_id,
    title: row.title,
    slug: row.slug,
    published: row.published,
    isHome: row.is_home,
    meta: (row.meta as unknown as PageMeta) ?? {},
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  })
}

function toBlockEntity(row: Tables<'blocks'>): Block {
  return Block.reconstitute({
    id: row.id,
    pageId: row.page_id,
    type: row.type as BlockType,
    props: (row.props as unknown as BlockProps) ?? {},
    settings: (row.settings as unknown as BlockSettings) ?? {},
    orderIndex: row.order_index,
    parentId: row.parent_id,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  })
}

export class SupabasePageRepository implements IPageRepository {
  async findById(id: string): Promise<Page | null> {
    const { data } = await supabase.from('pages').select('*').eq('id', id).single()
    return data ? toPageEntity(data) : null
  }

  async findBySiteId(siteId: string): Promise<Page[]> {
    const { data } = await supabase
      .from('pages')
      .select('*')
      .eq('site_id', siteId)
      .order('created_at', { ascending: true })
    return (data ?? []).map(toPageEntity)
  }

  async create(input: CreatePageInput): Promise<Page> {
    const { data, error } = await supabase
      .from('pages')
      .insert({ site_id: input.siteId, title: input.title, slug: input.slug, is_home: input.isHome ?? false })
      .select()
      .single()
    if (error ?? !data) throw new Error(error?.message ?? 'Failed to create page')
    return toPageEntity(data)
  }

  async update(id: string, patch: Partial<Page>): Promise<Page> {
    const db: TablesUpdate<'pages'> = {}
    if (patch.title !== undefined) db.title = patch.title
    if (patch.slug !== undefined) db.slug = patch.slug
    if (patch.published !== undefined) db.published = patch.published
    if (patch.meta !== undefined) db.meta = patch.meta as unknown as Json

    const { data, error } = await supabase.from('pages').update(db).eq('id', id).select().single()
    if (error ?? !data) throw new Error(error?.message ?? 'Failed to update page')
    return toPageEntity(data)
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('pages').delete().eq('id', id)
    if (error) throw new Error(error.message)
  }

  async getBlocks(pageId: string): Promise<Block[]> {
    const { data } = await supabase
      .from('blocks')
      .select('*')
      .eq('page_id', pageId)
      .is('parent_id', null)
      .order('order_index', { ascending: true })
    return (data ?? []).map(toBlockEntity)
  }

  async saveBlocks(pageId: string, blocks: Block[]): Promise<void> {
    await supabase.from('blocks').delete().eq('page_id', pageId)
    if (blocks.length === 0) return

    const { error } = await supabase.from('blocks').insert(
      blocks.map((b): TablesInsert<'blocks'> => ({
        id: b.id,
        page_id: b.pageId,
        type: b.type,
        props: b.props as unknown as Json,
        settings: b.settings as unknown as Json,
        order_index: b.orderIndex,
        parent_id: b.parentId,
      })),
    )
    if (error) throw new Error(error.message)
  }
}
